import {
  Button,
  Checkbox,
  cn,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@1trading/react';
import { Pencil, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  formatLimitAmount,
  getOrderLimitDrift,
  getPositionLimitDrift,
  isDefaultPriceCollar,
  isFuturesMarket,
  isUnsetOrderLimit,
  MARKET_STATE_OPTIONS,
  type Market,
  type MarketStateLabel,
  type OrderLimitMismatch,
  type PerpOrderLimit,
  POSITION_LIMIT_SEGMENT_LABELS,
  POSITION_LIMIT_SEGMENTS,
  type PositionLimit,
  toDisplayState,
  toPriceCollarPercentage,
} from '@/services/api/markets';

const LOADING_ROW_COUNT = 5;
/** select + 14 data columns */
const COLUMN_COUNT = 15;
const NOT_APPLICABLE = '—';

const STATE_SELECT_CLASSES = {
  active: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-500',
  inactive: 'border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-500',
} as const;

interface MarketsTableProps {
  markets: Market[];
  isLoading: boolean;
  /** Selection is controlled by the panel so it can be pruned per-tab and cleared after bulk updates */
  selectedIds: ReadonlySet<string>;
  onToggleRow: (id: string, isSelected: boolean) => void;
  onToggleAll: (isSelected: boolean) => void;
  onStateChange: (market: Market, label: MarketStateLabel) => void;
  onEditLimits: (market: Market) => void;
  /** Keyed by symbol; only futures markets have an entry */
  perpOrderLimitsBySymbol: ReadonlyMap<string, PerpOrderLimit>;
  isLimitsLoading: boolean;
  /** Keyed by symbol; empty when the exchange build predates position limits */
  positionLimitsBySymbol: ReadonlyMap<string, PositionLimit>;
  isPositionLimitsLoading: boolean;
}

/** Shown in the limit columns of a non-futures row */
const NotApplicableCell = () => <TableCell className="text-muted-foreground">{NOT_APPLICABLE}</TableCell>;
NotApplicableCell.displayName = 'NotApplicableCell';

const describePublished = (published: string | null | undefined): string => {
  if (published === undefined) return 'nothing yet (not re-seeded)';
  if (published === null) return 'no limit';
  return published;
};

interface DriftFlagProps {
  /** What the public API publishes against what the exchange enforces; null when they agree */
  message: string | null;
  children: ReactNode;
}

/**
 * A limit as the exchange enforces it, flagged when the public API publishes
 * something else. Saving the limits re-mirrors them and clears the flag.
 */
const DriftFlag = ({ message, children }: DriftFlagProps) => {
  if (message === null) return children;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex cursor-help items-center gap-1.5 text-amber-500">
          <TriangleAlert aria-label="Out of sync with the public API" className="size-4 shrink-0" />
          <span>{children}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent>{message}</TooltipContent>
    </Tooltip>
  );
};
DriftFlag.displayName = 'DriftFlag';

const RESYNC_LIMITS = 'Save the limits to resync.';

const describePublishedCollar = (published: string | null | undefined): string => {
  if (published === undefined) return 'nothing yet (not re-seeded)';
  if (published === null) return 'no collar';
  return `${published}%`;
};

const describePriceCollarDrift = (drift: OrderLimitMismatch | undefined): string | null =>
  drift ? `Public API publishes ${describePublishedCollar(drift.published)}; the exchange enforces ${drift.exchange}%. ${RESYNC_LIMITS}` : null;

const describeOrderValueDrift = (drift: OrderLimitMismatch | undefined): string | null =>
  drift
    ? `Public API publishes ${describePublished(drift.published)}; the exchange enforces ${isUnsetOrderLimit(drift.exchange) ? 'no limit' : formatLimitAmount(drift.exchange)}. ${RESYNC_LIMITS}`
    : null;

const describePositionSizeDrift = (drift: OrderLimitMismatch | undefined): string | null =>
  drift ? `Public API publishes ${describePublished(drift.published)}; the exchange enforces ${formatLimitAmount(drift.exchange)}. ${RESYNC_LIMITS}` : null;

interface OrderLimitCellsProps {
  market: Market;
  limits: PerpOrderLimit | undefined;
  isLoading: boolean;
}

/**
 * Price collar % / max order value for one futures row.
 * The exchange reports "0" for a value that was never set — that means "no
 * limit", so it is rendered as such rather than as a literal zero. Likewise a
 * 99% collar is the exchange's on-load default, flagged so an operator can
 * tell "never touched" from "deliberately wide". Both cells warn when the
 * public instruments record has drifted from the exchange.
 */
const OrderLimitCells = ({ market, limits, isLoading }: OrderLimitCellsProps) => {
  if (limits === undefined) {
    return isLoading ? (
      <>
        <TableCell>
          <Skeleton className="h-4 w-12" />
        </TableCell>
        <TableCell>
          <Skeleton className="h-4 w-20" />
        </TableCell>
      </>
    ) : (
      <>
        <NotApplicableCell />
        <NotApplicableCell />
      </>
    );
  }
  const drift = getOrderLimitDrift(market, limits);
  return (
    <>
      <TableCell className="whitespace-nowrap tabular-nums">
        <DriftFlag message={describePriceCollarDrift(drift?.priceCollar)}>
          {toPriceCollarPercentage(limits)}%{isDefaultPriceCollar(limits) ? <span className="text-muted-foreground"> (default)</span> : null}
        </DriftFlag>
      </TableCell>
      <TableCell className="whitespace-nowrap tabular-nums">
        <DriftFlag message={describeOrderValueDrift(drift?.maxOrderValue)}>
          {isUnsetOrderLimit(limits.maxOrderValue) ? <span className="text-muted-foreground">No limit</span> : formatLimitAmount(limits.maxOrderValue)}
        </DriftFlag>
      </TableCell>
    </>
  );
};
OrderLimitCells.displayName = 'OrderLimitCells';

interface PositionLimitCellsProps {
  market: Market;
  positionLimit: PositionLimit | undefined;
  isLoading: boolean;
}

/** Max position size per customer segment for one futures row. */
const PositionLimitCells = ({ market, positionLimit, isLoading }: PositionLimitCellsProps) => {
  if (positionLimit === undefined) {
    return (
      <>
        {POSITION_LIMIT_SEGMENTS.map((segment) =>
          isLoading ? (
            <TableCell key={segment}>
              <Skeleton className="h-4 w-20" />
            </TableCell>
          ) : (
            <NotApplicableCell key={segment} />
          ),
        )}
      </>
    );
  }
  const drift = getPositionLimitDrift(market, positionLimit);
  return (
    <>
      {POSITION_LIMIT_SEGMENTS.map((segment) => (
        <TableCell className="whitespace-nowrap tabular-nums" key={segment}>
          {/* A segment with no cap of its own shows the market default it falls back to */}
          <DriftFlag message={describePositionSizeDrift(drift?.[segment])}>
            {formatLimitAmount(positionLimit.segments[segment].effective)}
            {positionLimit.segments[segment].cap === null ? <span className="text-muted-foreground"> (default)</span> : null}
          </DriftFlag>
        </TableCell>
      ))}
    </>
  );
};
PositionLimitCells.displayName = 'PositionLimitCells';

/** One shared table for all five market-type tabs — only the filtered rows differ. */
export const MarketsTable = ({
  markets,
  isLoading,
  selectedIds,
  onToggleRow,
  onToggleAll,
  onStateChange,
  onEditLimits,
  perpOrderLimitsBySymbol,
  isLimitsLoading,
  positionLimitsBySymbol,
  isPositionLimitsLoading,
}: MarketsTableProps) => {
  const allSelected = markets.length > 0 && markets.every((market) => selectedIds.has(market.id));
  const someSelected = !allSelected && markets.some((market) => selectedIds.has(market.id));

  return (
    <div className="rounded-md border">
      <Table className="w-max min-w-full">
        <TableHeader>
          <TableRow>
            <TableHead>
              <Checkbox aria-label="Select all rows" checked={allSelected || (someSelected && 'indeterminate')} onCheckedChange={(checked) => onToggleAll(checked === true)} />
            </TableHead>
            <TableHead>Market ID</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Base/Quote</TableHead>
            <TableHead>Min Size</TableHead>
            <TableHead>Price Range</TableHead>
            <TableHead>Amount Precision</TableHead>
            <TableHead>Market Precision</TableHead>
            <TableHead>Price Collar %</TableHead>
            <TableHead>Max Order Value</TableHead>
            {POSITION_LIMIT_SEGMENTS.map((segment) => (
              <TableHead key={segment}>{POSITION_LIMIT_SEGMENT_LABELS[segment]} Max Position</TableHead>
            ))}
            <TableHead>Status</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading
            ? Array.from({ length: LOADING_ROW_COUNT }, (_, rowIndex) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton rows
                <TableRow key={rowIndex}>
                  {Array.from({ length: COLUMN_COUNT }, (_, cellIndex) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton cells
                    <TableCell key={cellIndex}>
                      <Skeleton className="h-4 w-full max-w-32" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : null}
          {!isLoading && markets.length === 0 ? (
            <TableRow>
              <TableCell className="h-24 text-center text-muted-foreground" colSpan={COLUMN_COUNT}>
                No markets
              </TableCell>
            </TableRow>
          ) : null}
          {!isLoading
            ? markets.map((market) => (
                <TableRow data-state={selectedIds.has(market.id) ? 'selected' : undefined} key={market.id}>
                  <TableCell>
                    <Checkbox aria-label={`Select ${market.id}`} checked={selectedIds.has(market.id)} onCheckedChange={(checked) => onToggleRow(market.id, checked === true)} />
                  </TableCell>
                  <TableCell className="font-medium">{market.id}</TableCell>
                  <TableCell>{market.type}</TableCell>
                  <TableCell>
                    {market.base.code}/{market.quote.code}
                  </TableCell>
                  <TableCell className="tabular-nums">{market.min_size}</TableCell>
                  <TableCell className="whitespace-nowrap tabular-nums">
                    {market.min_price} - {market.max_price}
                  </TableCell>
                  <TableCell className="tabular-nums">{market.amount_precision}</TableCell>
                  <TableCell className="tabular-nums">{market.market_precision}</TableCell>
                  {isFuturesMarket(market) ? (
                    <>
                      <OrderLimitCells isLoading={isLimitsLoading} limits={perpOrderLimitsBySymbol.get(market.id)} market={market} />
                      <PositionLimitCells isLoading={isPositionLimitsLoading} market={market} positionLimit={positionLimitsBySymbol.get(market.id)} />
                    </>
                  ) : (
                    <>
                      <NotApplicableCell />
                      <NotApplicableCell />
                      {POSITION_LIMIT_SEGMENTS.map((segment) => (
                        <NotApplicableCell key={segment} />
                      ))}
                    </>
                  )}
                  <TableCell>
                    {/* Controlled by query data — a cancelled/failed update reverts automatically */}
                    <Select onValueChange={(value) => onStateChange(market, value as MarketStateLabel)} value={toDisplayState(market.state)}>
                      <SelectTrigger
                        aria-label={`State of ${market.id}`}
                        className={cn('w-36', market.state === 'ACTIVE' ? STATE_SELECT_CLASSES.active : STATE_SELECT_CLASSES.inactive)}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MARKET_STATE_OPTIONS.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {/* One editor for the order and position limits — futures markets only */}
                    {isFuturesMarket(market) ? (
                      <Button aria-label={`Edit limits for ${market.id}`} onClick={() => onEditLimits(market)} size="icon" type="button" variant="ghost">
                        <Pencil className="size-4" />
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))
            : null}
        </TableBody>
      </Table>
    </div>
  );
};
MarketsTable.displayName = 'MarketsTable';
